import { useCallback, useEffect, useState } from 'react';
import { Button, Label, Icon } from '@iq/react-components';
import { useSelector } from 'react-redux';

import SimpleModal from '../SimpleModal';
import Indicator from '../panels/EventTimelinePanel/Indicator';

import { getTimezone } from '../../bundles/application';
import { formatSiteDatetime } from '../../datetimeUtils';
import { SEVERITIES, FORM_STATES } from '../../constants';

function showLabelledValue(value, label, transformer, showLabel = true, emptyValue = 'None') {
  if (!value && !showLabel) return null;
  let displayValue = emptyValue;
  if (value && (typeof value !== 'object' || value.length > 0)) {
    displayValue = transformer ? transformer(value) : value;
  }
  return (
    <>
      <Label>{label}</Label>
      <div className={`value-container ${displayValue === emptyValue ? 'empty-value' : ''}`}>
        {displayValue}
      </div>
    </>
  );
}

const IotEventModal = ({
  event: readOnlyEvent,
  hasHistory = false,
  onBack = () => null,
  onClose,
}) => {
  const [event, setEvent] = useState(readOnlyEvent);

  useEffect(() => {
    setEvent(readOnlyEvent);
  }, [readOnlyEvent]);

  // Wrap onClose to indicate no refresh is needed for read-only IOT events
  const handleClose = useCallback(() => {
    onClose({ refresh: false });
  }, [onClose]);

  const {
    description,
    timestamp,
    severity,
    message, // cloud (text)
    text, // onPremise
    origin, // cloud (pointId)
    pointId, // onPremise
    active,
    sender,
    remoteSource, // cloud (pointGroup)
    pointGroup, // onPremise
  } = event;

  const timezone = useSelector(getTimezone);

  // add checks here to make sure state has been updated
  if (!event?.timestamp) {
    return null;
  }

  return event?.timestamp ? (
    <SimpleModal
      size="l"
      onClose={handleClose}
      className={`event-modal-wrapper ${FORM_STATES.view}`}
      headerLeft={
        hasHistory ? (
          <Button
            design="text"
            tooltip="Back"
            onClick={onBack}
          >
            <Icon icon="he-previous" />
          </Button>
        ) : undefined
      }
      title={
        <div className="event-modal-header">
          <Indicator
            severity={severity}
            round={false}
            size={12}
          />
          <div>{message || text || 'IOT Event'}</div>
          {event?.isFalseAlarm && (
            <Button
              className="event-label"
              design="text"
              tooltip={
                'IdentiQ Analytics suggest that this event instance is not indicative of an elevated downtime risk.'
              }
              onClick={(event) => event.preventDefault()}
            >
              False
            </Button>
          )}
        </div>
      }
    >
      <div className={`event-modal ${FORM_STATES.view}`}>
        <div className={`top-section`}>
          <div className={`top-section__content iot`}>
            <div className="event-modal__attribute-group">
              {showLabelledValue(sender, 'Sender')}
            </div>
            <div className="event-modal__attribute-group">
              {showLabelledValue(active?.toString(), 'Active')}
            </div>
          </div>
        </div>
        <div className={`body-section iot`}>
          <div className="event-modal__column">
            <div className="event-modal__attribute-group">
              {showLabelledValue(description || message || text, 'Description')}
            </div>
            <div className="event-modal__attribute-group">
              {showLabelledValue(origin || pointId, 'Origin')}
            </div>
            <div className="event-modal__attribute-group">
              {showLabelledValue(remoteSource || pointGroup, 'Remote source')}
            </div>
          </div>
          <div className="event-modal__column">
            <div className="event-modal__attribute-group">
              {showLabelledValue(formatSiteDatetime(timestamp, timezone, true), 'Occurred at')}
            </div>
            <div className="event-modal__attribute-group">
              {showLabelledValue(SEVERITIES[severity], 'Severity')}
            </div>
          </div>
        </div>
      </div>
    </SimpleModal>
  ) : null;
};

export default IotEventModal;
